import React, { useContext, useEffect, useState } from "react";
import {
  GridContainer,
  InvoiceFormRootArea,
  GridItem,
} from "./InvoiceForm.styles";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Input,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import AuthContext from "../../../context/AuthContext";
import { useEffectOnce } from "../../../utils/effects";
import {
  getInvoiceById,
  createInvoice,
  updateInvoice,
} from "./InvoiceForm.thunk";
import {
  invoiceInitialValues,
  buildInvoiceValidationSchema,
  setInvoiceValues,
  toExpiresAtPayloadString,
} from "./InvoiceForm.resources";
import { INVOICE_NUMBER_INVALID_CHARS_MSG } from "../../../constants/invoiceNumberPattern";
import TextField from "../../textField/TextField";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import DateField from "../../dateField/DateField";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import {
  formatCurrencyAmount,
  parseAmountInput,
  amountToPlainInputString,
} from "../../../utils/currencyFormat";

const InvoiceForm = (props) => {
  const {
    setSubmenuMerchantTerminal,
    setSelectedOption,
    setNameSection,
    goBack,
  } = props;

  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);
  const merchantCurrency = (acquirerData?.currency_code || "USD").toUpperCase();
  const [previewInvoiceFileUrl, setPreviewInvoiceFileUrl] = useState(null);
  const [hadOriginalFile, setHadOriginalFile] = useState(false);
  const [amountFieldFocused, setAmountFieldFocused] = useState(false);
  const [amountDraft, setAmountDraft] = useState("");

  const {
    invoiceData,
    invoiceId,
    statusRetrieve,
    statusCreate,
    statusUpdate,
    errorRetrieve,
  } = useSelector((state) => state.invoiceForm);

  const { client_info_id } = useSelector((state) => state.invoices);

  const modeForm = invoiceId ? "update" : "create";
  const isLoading =
    statusRetrieve === "loading" ||
    statusCreate === "loading" ||
    statusUpdate === "loading";

  useEffectOnce(() => {
    if (invoiceId) {
      dispatch(getInvoiceById({ token, invoiceId }));
    }
  });

  // Effect to set form values when invoiceData changes (for editing)
  useEffect(() => {
    if (invoiceData && invoiceId) {
      setInvoiceValues({ invoiceData, setFieldValue });

      // If API returns an existing invoice file URL, set it in Formik and preview
      const fileUrl =
        invoiceData.invoice_file ||
        invoiceData.invoice_file_url ||
        invoiceData.file_url ||
        invoiceData.url ||
        null;
      if (fileUrl) {
        // Store the URL (string) in the form field so it's preserved in payload
        setFieldValue("invoice_file", fileUrl, true);
        setPreviewInvoiceFileUrl(fileUrl);
        setHadOriginalFile(true);
      } else {
        setHadOriginalFile(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData, invoiceId]);

  const handleGoBack = () => {
    if (goBack) {
      goBack();
    } else {
      setSubmenuMerchantTerminal("invoices");
      setSelectedOption("invoices");
      setNameSection("Merchant & Terminal - Invoices");
    }
  };

  const handleInvoiceFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      dispatch(
        showSnackbar({
          message: "The file must be a PDF",
          severity: "error",
        }),
      );
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      dispatch(
        showSnackbar({
          message: "The file size must be less than 10MB",
          severity: "error",
        }),
      );
      return;
    }

    // If previous preview was an object URL, revoke it to avoid leaks
    if (previewInvoiceFileUrl && previewInvoiceFileUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(previewInvoiceFileUrl);
      } catch (e) {
        // ignore
      }
    }

    setFieldValue("invoice_file", file, true);
    const fileUrl = URL.createObjectURL(file);
    setPreviewInvoiceFileUrl(fileUrl);
    // If user uploads a new file, they're replacing (not deleting), so reset the flag
    setHadOriginalFile(false);
  };

  const handleDeleteInvoiceFile = () => {
    if (previewInvoiceFileUrl && previewInvoiceFileUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(previewInvoiceFileUrl);
      } catch (e) {
        // ignore
      }
    }
    setPreviewInvoiceFileUrl(null);
    setFieldValue("invoice_file", null, true);
  };

  const handleInvoiceNumberChange = (event) => {
    const { value } = event.target;
    const sanitizedValue = value.replace(/\s+/g, "").replace(/[^A-Za-z0-9._:-]/g, "");
    setFieldValue("invoice_number", sanitizedValue, true);
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
  } = useFormik({
    initialValues: {
      ...invoiceInitialValues,
      currency: merchantCurrency,
      client_info_id: client_info_id || null,
    },
    validationSchema: buildInvoiceValidationSchema(
      merchantCurrency,
      modeForm === "create",
    ),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { status, no_expiration, expires_at, ...valuesWithoutMeta } =
          values;
        const formData = {
          ...valuesWithoutMeta,
          merchant_id: acquirerData?.merchantID || "",
          currency: valuesWithoutMeta.currency
            ? valuesWithoutMeta.currency.trim().toUpperCase()
            : "",
          amount:
            typeof valuesWithoutMeta.amount === "number"
              ? valuesWithoutMeta.amount
              : parseFloat(valuesWithoutMeta.amount),
          expires_at: toExpiresAtPayloadString(expires_at, no_expiration),
        };

        // If editing and user deleted the file (had original file but now it's null)
        if (
          modeForm === "update" &&
          hadOriginalFile &&
          (valuesWithoutMeta.invoice_file === null ||
            valuesWithoutMeta.invoice_file === undefined)
        ) {
          formData.invoice_file_action = "nrop";
        }

        if (modeForm === "create") {
          await dispatch(
            createInvoice({ token, handleGoBack, ...formData }),
          ).unwrap();
        } else {
          await dispatch(
            updateInvoice({
              token,
              handleGoBack,
              invoiceId,
              ...formData,
            }),
          ).unwrap();
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        // Don't set setSubmitting(false) here - let Formik handle it
        // This allows Formik to properly handle validation errors
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleNoExpirationChange = (event) => {
    const checked = event.target.checked;
    setFieldValue("no_expiration", checked, true);
    if (checked) {
      setFieldValue("expires_at", "", true);
      setFieldTouched("expires_at", false, false);
    }
  };

  // Effect to set client_info_id when it's available (after useFormik is initialized)
  useEffect(() => {
    if (client_info_id && values.client_info_id !== client_info_id) {
      setFieldValue("client_info_id", client_info_id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_info_id]);

  // Keep create invoices aligned with merchant profile currency.
  useEffect(() => {
    if (modeForm === "create" && values.currency !== merchantCurrency) {
      setFieldValue("currency", merchantCurrency, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeForm, merchantCurrency]);

  const invoiceCurrencyCode =
    (values.currency && String(values.currency).trim().toUpperCase()) ||
    merchantCurrency;

  const commitAmountFromDraft = async () => {
    if (!amountFieldFocused) return;
    const raw = amountDraft;
    const parsed = parseAmountInput(raw);
    if (raw.trim() === "") {
      await setFieldValue("amount", "", true);
    } else if (Number.isFinite(parsed)) {
      await setFieldValue("amount", parsed, true);
    } else {
      await setFieldValue("amount", raw, true);
    }
    setFieldTouched("amount", true);
    setAmountFieldFocused(false);
    setAmountDraft("");
  };

  const handleAmountFocus = () => {
    setAmountFieldFocused(true);
    setAmountDraft(amountToPlainInputString(values.amount));
  };

  const handleAmountChange = (e) => {
    setAmountDraft(e.target.value);
  };

  const handleAmountBlur = () => {
    void commitAmountFromDraft();
  };

  const amountInputDisplay = amountFieldFocused
    ? amountDraft
    : formatCurrencyAmount(values.amount, invoiceCurrencyCode, {
        emptyDisplay: "",
      });

  // Loading state while retrieving data
  if (statusRetrieve === "loading") {
    return (
      <InvoiceFormRootArea>
        <GridContainer>
          <GridItem xs={12}>
            <GridContainer
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <GridItem
                xs={12}
                style={{ textAlign: "center", padding: "50px" }}
              >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Loading invoice data...
                </Typography>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </InvoiceFormRootArea>
    );
  }

  // Error state when retrieving data fails
  if (statusRetrieve === "failed") {
    return (
      <InvoiceFormRootArea>
        <GridContainer>
          <GridItem xs={12}>
            <GridContainer spacing={2}>
              <GridItem xs={12}>
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Error Loading Invoice
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {errorRetrieve ||
                      "An error occurred while loading the invoice data."}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGoBack}
                    startIcon={<ArrowBackIcon />}
                  >
                    Go Back
                  </Button>
                </Box>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </InvoiceFormRootArea>
    );
  }

  return (
    <InvoiceFormRootArea>
      <GridContainer>
        <GridContainer justifyContent="space-between" alignItems="center">
          <GridItem xs={10}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
              {modeForm === "create" ? "Create Invoice" : "Edit Invoice"}
            </Typography>
          </GridItem>
          <GridItem xs={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleGoBack}
              size="small"
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </GridItem>
        </GridContainer>

        <GridItem xs={12}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await commitAmountFromDraft();
              handleSubmit(e);
            }}
          >
            <GridContainer spacing={2}>
              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="invoice_number"
                  name="invoice_number"
                  fullWidth
                  label="Invoice Number"
                  value={values.invoice_number}
                  onChange={handleInvoiceNumberChange}
                  onBlur={handleBlur}
                  inputProps={{ pattern: "[A-Za-z0-9._:-]+" }}
                  required
                  error={errors.invoice_number}
                  touched={touched.invoice_number}
                  helperText={INVOICE_NUMBER_INVALID_CHARS_MSG}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="amount"
                  name="amount"
                  fullWidth
                  label="Amount"
                  inputProps={{
                    min: 0,
                    step: "0.01",
                    inputMode: "decimal",
                    autoComplete: "off",
                  }}
                  value={amountInputDisplay}
                  onChange={handleAmountChange}
                  onFocus={handleAmountFocus}
                  onBlur={handleAmountBlur}
                  required
                  error={errors.amount}
                  touched={touched.amount}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <CurrencySelectField
                  id="currency"
                  name="currency"
                  fullWidth
                  label="Currency"
                  value={invoiceCurrencyCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled
                  error={errors.currency}
                  touched={touched.currency}
                  helperText={
                    modeForm === "create"
                      ? "Merchant transaction currency (from your profile)"
                      : "Invoice currency"
                  }
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    minHeight: 56,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="no_expiration"
                        checked={Boolean(values.no_expiration)}
                        onChange={handleNoExpirationChange}
                        disabled={isLoading || isSubmitting}
                      />
                    }
                    label="No expiration date"
                  />
                </Box>
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <DateField
                  id="expires_at"
                  name="expires_at"
                  fullWidth
                  label="Expiration Date"
                  value={values.expires_at}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  setFieldTouched={setFieldTouched}
                  disabled={
                    isLoading || isSubmitting || Boolean(values.no_expiration)
                  }
                  error={errors.expires_at}
                  touched={touched.expires_at}
                  minDate={moment().startOf("day")}
                  helperText="Optional. Invoice cannot be paid after the end of the selected day."
                />
              </GridItem>

              <GridItem xs={12} padding="15px 15px 15px 0">
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Invoice PDF (optional)
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <label htmlFor="invoice-file-upload">
                    <Input
                      id="invoice-file-upload"
                      type="file"
                      accept="application/pdf"
                      onChange={handleInvoiceFileChange}
                      disabled={isLoading || isSubmitting}
                      sx={{ display: "none" }}
                    />
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={isLoading || isSubmitting}
                    >
                      Attach PDF
                    </Button>
                  </label>
                  {(previewInvoiceFileUrl || values.invoice_file) && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteInvoiceFile}
                      disabled={isLoading || isSubmitting}
                    >
                      Remove PDF
                    </Button>
                  )}
                </Box>
                {(previewInvoiceFileUrl || values.invoice_file) && (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {values.invoice_file instanceof File
                          ? values.invoice_file.name
                          : "Invoice PDF preview"}
                      </Typography>
                    </Box>
                    {previewInvoiceFileUrl && (
                      <Box
                        component="iframe"
                        src={previewInvoiceFileUrl}
                        sx={{ width: "100%", height: 400, border: "none" }}
                        title="Invoice PDF preview"
                      />
                    )}
                  </Box>
                )}
              </GridItem>

              <GridItem xs={12} padding="15px 15px 15px 0">
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleGoBack}
                    disabled={isLoading || isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading || isSubmitting}
                  >
                    {isLoading || isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {modeForm === "create" ? "Creating..." : "Updating..."}
                      </>
                    ) : modeForm === "create" ? (
                      "E-mail Invoice"
                    ) : (
                      "Update Invoice"
                    )}
                  </Button>
                </Box>
              </GridItem>
            </GridContainer>
          </form>
        </GridItem>
      </GridContainer>
    </InvoiceFormRootArea>
  );
};

export default InvoiceForm;
